import React from 'react';
import { Layers, TrendingUp, Gauge, Flame } from 'lucide-react';
import { BULLISH_THRESHOLD, formatInr, formatSignedPercent } from '../../utils/gmpBoard';

const StatCard = ({ label, icon: Icon, iconTone, value, valueTone = 'text-slate-900', chip, chipTone, footnote, loading }) => (
  <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconTone}`}>
        <Icon className="w-4 h-4" />
      </span>
    </div>
    {loading ? (
      <div className="animate-pulse mt-2 space-y-2.5">
        <div className="w-32 h-7 bg-slate-200 rounded" />
        <div className="w-44 h-3 bg-slate-100 rounded" />
      </div>
    ) : (
      <>
        <div className="flex flex-wrap items-baseline gap-2 mt-1.5">
          <span className={`text-2xl sm:text-[28px] font-extrabold tracking-tight ${valueTone}`}>{value}</span>
          {chip && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold border ${chipTone}`}>
              {chip}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1.5 truncate" title={footnote}>
          {footnote}
        </p>
      </>
    )}
  </div>
);

// variant 'sme' labels the cards for the SME-only board (/sme-ipo-gmp)
const LABELS = {
  all: { total: 'Total Tracked Issues', top: 'Highest GMP Premium', average: 'Average Premium (Open)', sentiment: 'Market Sentiment Bias' },
  sme: { total: 'Tracked SME Issues', top: 'Highest SME GMP', average: 'Average SME Premium (Open)', sentiment: 'SME Sentiment Bias' },
};

export const GmpStatCards = ({ stats, loading, variant = 'all' }) => {
  const { top, avgOpenPremium, bullishShare, avgFireRating } = stats;
  const labels = LABELS[variant] || LABELS.all;
  const isSme = variant === 'sme';

  const demandLabel =
    avgOpenPremium === null ? null : avgOpenPremium >= 25 ? 'High Demand' : avgOpenPremium >= 10 ? 'Moderate' : 'Soft';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <StatCard
        loading={loading}
        label={labels.total}
        icon={Layers}
        iconTone="bg-teal-50 text-[#008080]"
        value={`${stats.total} Issues`}
        chip={isSme ? `${stats.nseCount} NSE / ${stats.bseCount} BSE` : `${stats.mainboardCount} Main / ${stats.smeCount} SME`}
        chipTone="bg-teal-50 text-[#008080] border-teal-200"
        footnote={`${stats.quotedCount} with a live grey market quote`}
      />
      <StatCard
        loading={loading}
        label={labels.top}
        icon={TrendingUp}
        iconTone="bg-emerald-50 text-emerald-600"
        value={top ? formatSignedPercent(top.gmpPercent) : '—'}
        valueTone="text-emerald-600"
        chip={top ? `${formatInr(top.gmp)} GMP` : null}
        chipTone="bg-emerald-50 text-emerald-700 border-emerald-200"
        footnote={
          top
            ? `${top.name} · ${isSme ? top.exchange || 'SME' : top.board}${top.price !== null ? ` · ${formatInr(top.price)} issue` : ''}`
            : 'No active quotes yet'
        }
      />
      <StatCard
        loading={loading}
        label={labels.average}
        icon={Gauge}
        iconTone="bg-sky-50 text-sky-600"
        value={avgOpenPremium !== null ? formatSignedPercent(avgOpenPremium) : '—'}
        valueTone="text-sky-700"
        chip={demandLabel}
        chipTone="bg-sky-50 text-sky-700 border-sky-200"
        footnote={
          stats.openQuotedCount
            ? `Weighted by issue size across ${stats.openQuotedCount} open issues`
            : 'No open issues with a GMP quote'
        }
      />
      <StatCard
        loading={loading}
        label={labels.sentiment}
        icon={Flame}
        iconTone="bg-amber-50 text-amber-600"
        value={bullishShare !== null ? `${Math.round(bullishShare)}% Bullish` : '—'}
        valueTone="text-amber-600"
        chip={avgFireRating !== null ? `Fire Rating: ${avgFireRating.toFixed(1)}/5` : null}
        chipTone="bg-amber-50 text-amber-700 border-amber-200"
        footnote={
          stats.quotedCount
            ? `${stats.bullishCount} of ${stats.quotedCount} quoted issues at ${BULLISH_THRESHOLD}%+ premium`
            : 'Waiting for grey market quotes'
        }
      />
    </div>
  );
};

export default GmpStatCards;
